// The app's ONE navigation unit. This used to be two things competing for the
// same corner: a `fixed top-4 right-4` cluster in `App.tsx` (membership + theme)
// and every reading surface's own header row (`UnitNavButtons`, bookmarks, view
// modes). They overlapped — which is why those headers all carried a `pr-16`
// reservation, a number sized for a lone `ModeToggle` and long since outgrown by
// the membership link that landed beside it.
//
// One component, two renderings:
//
//   • `floating` — the fixed corner cluster, for pages with no header of their
//     own (library, purchase, admin console, not-found).
//   • `inline` — the same controls as ordinary flex children, for surfaces that
//     DO own a header. `children` is that surface's own navigation, rendered
//     before the global controls and set off by a separator, so a single row
//     holds the lot and nothing can overlap anything.
//
// The membership link moved into the account menu rather than staying an inline
// text button. It is still the only route to cancelling, so it still has to be
// reachable from everywhere — an account menu that is present on every page is
// that, and it costs one icon of width instead of ten characters, which is what
// let the reader's header absorb these controls at all.

import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "convex/react";
import {
  CreditCard,
  LogIn,
  LogOut,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { currentPath } from "@bindery/logging/current-path";
import { useLogger } from "@/logging";
import { Button } from "@bindery/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bindery/ui/components/ui/dropdown-menu";
import { Separator } from "@bindery/ui/components/ui/separator";
import { ModeToggle } from "@/components/mode-toggle";
import { signIn, signOut, useAuthState } from "@/lib/auth";
import { cn } from "@bindery/ui/lib/utils";
import { api } from "@bindery/convex-retells/_generated/api";

/**
 * Account menu + the sign-in affordance beside it.
 *
 * Sign IN is a plain button, not a menu item: signing in was previously
 * reachable only by tripping over a gate (the paywall, the gloss popover, the
 * admin console's own prompt), so a reader who simply wanted their bookmarks
 * back had nowhere to click. Sign OUT stays inside the menu — it is a rare,
 * deliberate act and does not deserve permanent pixels.
 */
function AccountControls() {
  // `useAuthState`, not `useIsAuthenticated`: this renders in the corner of
  // every route on first paint, and treating "not resolved yet" as "signed
  // out" would show a signed-in reader a Sign-in button for the length of the
  // boot token fetch.
  const auth = useAuthState();
  // These are now the most reachable sign-in AND the only sign-out on the
  // site, so the auth funnel would otherwise gain a large unattributed source
  // (logging policy §2: rare, high-value user actions earn a wide event). The
  // three older sign-in affordances name where they were tripped over —
  // `paywall.signin`, `subscribe.signin`, `gloss.signin_click`; this one names
  // the surface the reader was on when they chose to.
  const log = useLogger();
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });
  // Skipped while anonymous: `myRole` is `null` for every signed-out caller, so
  // asking would buy nothing and put a Convex subscription on every reader's
  // page load. `undefined` is the in-flight state — the admin entry appears
  // when the answer arrives, which is the right way round for a link most
  // readers must never see.
  const adminRole = useQuery(
    api.admins.myRole,
    auth === "authenticated" ? {} : "skip",
  );

  return (
    <>
      {auth === "anonymous" && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground font-serif text-xs tracking-[0.2em] uppercase"
          onClick={() => {
            logRef.current.event("nav.signin", {
              path: currentPath(),
            });
            signIn();
          }}
        >
          <LogIn className="size-3.5" />
          {/* Icon-only on phones. Inline, these controls share a header row
              with the book's title, and ten characters of "SIGN IN" is the
              difference between a readable title and an ellipsis. */}
          <span className="hidden sm:inline">Sign in</span>
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Account">
            <UserRound className="size-[1.1rem]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-muted-foreground font-serif text-[0.65rem] tracking-[0.25em] uppercase">
            Reading room
          </DropdownMenuLabel>
          <DropdownMenuItem asChild>
            <Link to="/subscribe">
              <CreditCard />
              Membership
            </Link>
          </DropdownMenuItem>
          {adminRole != null && (
            <DropdownMenuItem asChild>
              <Link to="/admin">
                <ShieldCheck />
                Editorial console
              </Link>
            </DropdownMenuItem>
          )}
          {auth === "authenticated" && (
            <>
              <DropdownMenuSeparator />
              {/* `onSelect`, not `onClick`: it is the handler Radix fires for
                  both pointer and keyboard activation of a menu item. */}
              <DropdownMenuItem
                onSelect={() => {
                  logRef.current.event("nav.signout", {
                    path: currentPath(),
                  });
                  signOut();
                }}
              >
                <LogOut />
                Sign out
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

/**
 * A discriminated union rather than two independent props: `floating` is a
 * fixed corner cluster with nothing around it, so handing it a surface's own
 * toolbar would pin that toolbar to the viewport. Only `inline` can carry
 * children, and the type says so.
 */
type AppNavProps =
  | {
      /** Pinned to the viewport's top-right corner, for a page with no header
       *  of its own. Routed there by sitting under `ChromeLayout` (App.tsx). */
      variant: "floating";
      children?: never;
    }
  | {
      /** Plain flex children of whatever header hosts it. Nothing is derived
       *  from the route — a surface that owns a header asks for this. */
      variant: "inline";
      /** The host surface's own controls — prev/next, bookmarks, view modes.
       *  Rendered ahead of the site controls and set off from them. */
      children?: ReactNode;
    };

export function AppNav({ variant, children }: AppNavProps) {
  // `false` and `null` are what `{cond && x}` and `{cond ? x : null}` hand us,
  // and both render nothing — so neither should earn a separator with nothing
  // on its far side.
  const hasOwnControls =
    children !== undefined && children !== null && children !== false;

  return (
    <div
      className={cn(
        "flex items-center gap-2",
        variant === "floating" && "fixed top-4 right-4 z-50",
      )}
    >
      {hasOwnControls && (
        <>
          {children}
          <Separator orientation="vertical" className="h-6" />
        </>
      )}
      {/* The landmark covers the SITE controls only. `children` is the host
          surface's own chrome — a text-size slider and a bookmark toggle are
          not site navigation, and announcing them under one would make the
          landmark useless to anyone navigating by it. */}
      <nav aria-label="Site" className="flex items-center gap-2">
        <AccountControls />
        <ModeToggle />
      </nav>
    </div>
  );
}
